// Command deck — ONE control line: chapter nav (where am I) on the left,
// time range + Report on the right. Channel filtering lives where the
// channels live (the lane's channel cards); active filters show as removable
// chips on a second line only when present.

import { useEffect, useRef } from "react"
import { Printer, XClose } from "@untitledui/icons"
import { cx } from "@/lib/utils/cx"
import { DesignSelect } from "@/components/ui/design-select"
import { PillTabs } from "./room-chrome"
import { RANGES, type RangeKey } from "./room-model"
import type { ChapterId } from "./room-model"

type Chip = { key: string; label: string; clear: () => void }
type AdvisorOption = { id: string; name: string; sub?: string }

export function CommandDeck({
  chapters, activeChapter, onNavClick,
  rangeLabel, onRange,
  advisors, departments, advisorId, onAdvisor,
  chips, onClearDims, onPrint,
  scrollContainerRef,
}: {
  chapters: { id: ChapterId; label: string }[]
  activeChapter: string
  onNavClick: (id: ChapterId) => void
  rangeLabel: string
  onRange: (key: RangeKey) => void
  advisors: AdvisorOption[]
  departments: string[]
  advisorId: string
  onAdvisor: (id: string) => void
  chips: Chip[]
  onClearDims: () => void
  onPrint: () => void
  scrollContainerRef: React.RefObject<HTMLElement | null>
}) {
  const deckRef = useRef<HTMLDivElement | null>(null)

  // Publish the deck's live height so sections' scroll-margin-top lands just
  // below it (the chips row appears/disappears → height varies).
  useEffect(() => {
    const deck = deckRef.current
    const container = scrollContainerRef.current
    if (!deck || !container) return
    const write = () => container.style.setProperty("--room-rail-h", `${deck.offsetHeight}px`)
    write()
    const ro = new ResizeObserver(write)
    ro.observe(deck)
    return () => ro.disconnect()
  }, [scrollContainerRef])

  return (
    <div className="sticky top-0 z-20 mb-6 print:hidden">
      <div ref={deckRef} className="animate-element rounded-2xl border border-[#ECEBE6] bg-white/95 backdrop-blur-md shadow-sm">
        {/* The one line: chapters · range · report */}
        <div className="flex items-center gap-2 pl-1.5 pr-3 py-1.5">
          <nav aria-label="Report chapters" className="flex items-center gap-0.5 overflow-x-auto scrollbar-hide flex-1 min-w-0">
            {chapters.map(c => {
              const active = activeChapter === c.id
              return (
                <button key={c.id} onClick={() => onNavClick(c.id)} aria-current={active ? "true" : undefined}
                  className={cx(
                    "relative shrink-0 px-3 py-2 text-[13px] font-semibold whitespace-nowrap transition-colors",
                    active ? "text-zinc-900" : "text-[#8B897E] hover:text-zinc-700",
                  )}>
                  {c.label}
                  {active && <span className="absolute left-3 right-3 -bottom-px h-0.5 rounded-full bg-[#0F6E56]" />}
                </button>
              )
            })}
          </nav>
          {/* Who ran it — scopes the whole room, same filter the people
              table toggles by row click. "" = everyone. */}
          {advisors.length > 0 && (
            <DesignSelect.Root
              value={advisorId === "" ? "all" : advisorId}
              onValueChange={v => onAdvisor(v === "all" ? "" : v)}
            >
              <DesignSelect.Trigger className="h-8 rounded-full border-[#E3E1DA] bg-white px-3 text-[12px] font-semibold text-[#6E6C64] hover:text-zinc-900 hover:border-[#D6D3C8] hover:bg-white">
                <DesignSelect.Value placeholder="Everyone" />
              </DesignSelect.Trigger>
              <DesignSelect.Content>
                <DesignSelect.Item value="all">Everyone</DesignSelect.Item>
                {departments.length > 0 && (
                  <p className="px-2.5 pt-2 pb-1 text-[9.5px] font-bold uppercase tracking-[0.12em] text-zinc-400">
                    Departments
                  </p>
                )}
                {departments.map(d => (
                  <DesignSelect.Item key={`dept:${d}`} value={`dept:${d}`}>
                    {d}
                  </DesignSelect.Item>
                ))}
                <p className="px-2.5 pt-2 pb-1 text-[9.5px] font-bold uppercase tracking-[0.12em] text-zinc-400">
                  People
                </p>
                {advisors.map(a => (
                  <DesignSelect.Item key={a.id} value={a.id} sub={a.sub}>
                    {a.name}
                  </DesignSelect.Item>
                ))}
              </DesignSelect.Content>
            </DesignSelect.Root>
          )}
          <div className="shrink-0">
            <PillTabs size="sm" ariaLabel="Time range"
              items={(Object.keys(RANGES) as RangeKey[]).map(k => ({ key: k, label: RANGES[k].label }))}
              active={(Object.keys(RANGES) as RangeKey[]).find(k => RANGES[k].label === rangeLabel) ?? "30d"}
              onSelect={k => onRange(k)} />
          </div>
          <button onClick={onPrint}
            className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#E3E1DA] text-[12px] font-semibold text-[#6E6C64] hover:border-[#D6D3C8] hover:text-zinc-900 transition-colors"
            title="Print / export this report">
            <Printer className="size-3.5" aria-hidden /> Report
          </button>
        </div>

        {/* Active filters — only when present */}
        {chips.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap px-3.5 py-2 border-t border-[#ECEBE6]">
            <span className="text-[10px] font-bold uppercase tracking-wider text-[#8B897E] mr-0.5">Filters</span>
            {chips.map(c => (
              <button key={c.key} onClick={c.clear}
                className="inline-flex items-center gap-1.5 pl-3 pr-2 py-1 rounded-full bg-[#E8F8F2] border border-[#9FE1CB] text-xs font-medium text-[#0F6E56] hover:bg-[#d7f2e6] transition-colors">
                {c.label} <XClose className="size-3" aria-hidden />
              </button>
            ))}
            <button onClick={onClearDims} className="text-xs font-medium text-[#8B897E] hover:text-zinc-700 transition-colors ml-1">
              Clear all
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
